import { TimetableRelationshipProperties } from '@bp/planung-graphql-types';
import {
  AddIcon,
  Button,
  ButtonGroup,
  Card,
  DuplicateIcon,
  Grid,
  GridColumn,
  GridRow,
  ImportIcon,
  LazyLoader,
  Modal,
  Skeleton,
  Tab,
  Tooltip,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useClassesTable } from '../../../../../../components/Classes/hooks/useClassesTable';
import { ConstraintsForm } from '../../../../../../components/Constraints/Form/ConstraintsForm';
import { ConstraintsFormInitialValuesType, constraintOrder } from '../../../../../../components/Constraints/types';
import { useTimetableStore } from '../../../../../../components/TimetableGrid/TimetableProvider';
import { useTimetableClasses } from '../../../../../../components/TimetableVersion/hooks/useTimetableClasses';
import { TimetableVersionClassesMatrix } from '../../../../../../components/TimetableVersion/TimetableVersionDataAvailabilityMatrix/TimetableVersionClassesMatrix';
import { TimetableVersionClassesConstraints } from '../../../../../../components/TimetableVersion/TimetableVersionDataConditions/TimetableVersionClassesConditions';
import { TimetableVersionClassesTable } from '../../../../../../components/TimetableVersion/TimetableVersionDataTables/TimetableVersionClassesTable';
import { TimetableVersionImportClasses } from '../../../../../../components/TimetableVersion/TimetableVersionImportClasses/TimetableVersionImportClasses';
import { useMemorizedCacheTag } from '../../../../../../hooks/useMemorizedCacheTag';
import {
  useClassConstraintsQuery,
  useUpdateTimetableVersionClassConditionsMutation,
} from '../../../../../../types/planung-graphql-client-defs';
import { createLessonFilterUrl } from '../../../../../../utils/create-lesson-filter-url';
import { removeNullAndRelatedFields } from '../../../../../../utils/removeNullAndRelatedFields';
import { availabilityStore } from '../../TimetableVersion';
import styles from '../TimetableVersionData.module.scss';

export const PlanTimetableClasses = observer(() => {
  const { t } = useTranslation();
  const { versionUuid } = useParams();
  const navigate = useNavigate();
  const store = useTimetableStore();
  const timeAvailabilityStore = store.getTimeAvailabilityStore();

  const versionClassesUuids: string[] = useMemo(() => Array.from(store.classes.keys()), [store.classes]);

  const [loading, setLoading] = useState(false);

  const { data } = useClassesTable();
  const delta = data.filter((c) => !versionClassesUuids.includes(c.uuid));

  const { removeClasses } = useTimetableClasses(versionUuid ?? '');

  const [selectedUuid, setSelectedUuid] = useState<string>(data[0] ? data[0].uuid : '');

  const onRemove = async (uuids: string[]) => {
    if (store.readonly) return;
    setSelectedUuid(data[0] ? data[0].uuid : '');
    await removeClasses(uuids);
    store.removeClasses(uuids);
  };

  const [tab, setTab] = useState<'availability' | 'condition' | 'division'>('availability');
  const [constraintModalOpen, setConstraintModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const [, updateClassCondition] = useUpdateTimetableVersionClassConditionsMutation();

  const constraintsContext = useMemorizedCacheTag('CLASSES_CONSTRAINTS');

  const timeGridEntries = store.timeGridEntries ?? [];

  const [{ data: constraintsResult }] = useClassConstraintsQuery({
    variables: {
      versionUuid: versionUuid ?? '',
      uuid: selectedUuid ?? '',
    },
    context: constraintsContext,
  });

  const constraintsData = useMemo(() => {
    return JSON.parse(
      constraintsResult?.timetableVersions[0]?.classesConnection?.edges[0]?.properties.constraints ??
        JSON.stringify({
          comment: '',
        }),
    ) as ConstraintsFormInitialValuesType;
  }, [constraintsResult]);

  async function handleMatrixChange(items: TimetableRelationshipProperties) {
    if (store.readonly) return;
    if (versionUuid && selectedUuid) {
      store.removeConflicts();
      setLoading(true);
      await availabilityStore?.setAvailability('class', selectedUuid, items, versionUuid);
      timeAvailabilityStore?.setResourceAvailability(
        [
          {
            uuid: selectedUuid,
            days: {
              mon: items.mon,
              tue: items.tue,
              wed: items.wed,
              thu: items.thu,
              fri: items.fri,
              sat: items.sat,
              sun: items.sun,
            },
          },
        ],
        'class',
      );
      store.setConflicts();
      setLoading(false);
    }
  }

  async function handleConditionsChange(data: ConstraintsFormInitialValuesType) {
    if (versionUuid && selectedUuid) {
      // clean up data
      const removeNullAndRelatedField = removeNullAndRelatedFields(data, constraintOrder, 'Priority');
      const { error } = await updateClassCondition(
        {
          versionUuid: versionUuid,
          classUuid: selectedUuid ?? '',
          data: JSON.stringify(removeNullAndRelatedField),
        },
        constraintsContext,
      );
      return !error;
    }
    return false;
  }

  return (
    <>
      <Grid>
        <GridRow spacingBottom='none'>
          <Button
            hierarchy='tertiary'
            disabled={!delta?.length || store.readonly}
            icon={<ImportIcon />}
            onClick={() => setImportModalOpen(true)}
            style={{ marginLeft: 'auto' }}
          >
            {t('timetableVersion.import', { import: t('classes.title', { count: 2 }) })}
          </Button>
        </GridRow>
        <GridRow>
          <GridColumn width={6}>
            {versionUuid && (
              <TimetableVersionClassesTable
                onRemove={onRemove}
                onRowSelection={(selected) => setSelectedUuid(selected.uuid)}
                versionUuid={versionUuid}
                versionClassesUuids={versionClassesUuids}
                onShowLessonClick={(uuid) => {
                  if (versionUuid) {
                    navigate(
                      createLessonFilterUrl({
                        context: 'stundenplan',
                        filterValue: uuid,
                        versionUuid: versionUuid,
                        column: 'classes',
                      }),
                    );
                  }
                }}
              />
            )}
          </GridColumn>
          <GridColumn width={6}>
            {versionClassesUuids.length !== 0 && timeGridEntries && (
              <Card className={styles['version-data']} contentPadding='none' fitContent>
                <small>
                  {t('timetableVersion.specificInfo', { for: data.find((c) => c.uuid === selectedUuid)?.name ?? '' })}
                </small>
                <div className={styles['mini-tab']}>
                  <Tab
                    value={tab}
                    key={selectedUuid}
                    onValueChange={(content) => setTab(content as 'availability' | 'condition' | 'division')}
                    className={styles.tabs}
                    tabs={[
                      {
                        title: t('availability.title', { count: 2 }),
                        value: 'availability',
                        content: (
                          <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
                            <TimetableVersionClassesMatrix
                              versionUuid={versionUuid}
                              selectedUuid={selectedUuid}
                              timeGridEntries={timeGridEntries}
                              loading={loading}
                              onChange={handleMatrixChange}
                            />
                          </Suspense>
                        ),
                      },
                      {
                        title: t('conditions.title', { count: 2 }),
                        value: 'condition',
                        content: (
                          <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
                            <TimetableVersionClassesConstraints
                              constraintsData={constraintsData}
                              onChange={handleConditionsChange}
                            />
                          </Suspense>
                        ),
                      },
                      {
                        title: t('divisions.title', { count: 2 }),
                        value: 'division',
                        content: (
                          <div className='px-6 py-4'>
                            <Skeleton />
                          </div>
                        ),
                      },
                    ]}
                  />
                  <div className={styles['additional-actions']}>
                    {tab === 'availability' && (
                      <Tooltip content={t('availability.duplicate')}>
                        <Button hierarchy='ghost' icon={<DuplicateIcon />} disabled />
                      </Tooltip>
                    )}
                    {tab === 'condition' && (
                      <ButtonGroup>
                        <Tooltip content={t('constraint.duplicate')}>
                          <Button hierarchy='ghost' icon={<DuplicateIcon />} disabled />
                        </Tooltip>
                        <Tooltip content={t('constraint.edit')}>
                          <Button
                            hierarchy='ghost'
                            icon={<AddIcon />}
                            onClick={() => setConstraintModalOpen(true)}
                            disabled={store.readonly}
                          />
                        </Tooltip>
                      </ButtonGroup>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </GridColumn>
        </GridRow>
      </Grid>

      <Modal
        isOpen={constraintModalOpen}
        onRequestClose={() => setConstraintModalOpen(false)}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        title={t('conditions.for', { for: data.find((c) => c.uuid === selectedUuid)?.name ?? '' })}
      >
        <ConstraintsForm
          constraints={constraintsData}
          timeGridEntries={timeGridEntries}
          onSubmit={(values) => handleConditionsChange(values)}
          onClose={() => setConstraintModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={importModalOpen}
        onRequestClose={() => setImportModalOpen(false)}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        title={t('timetableVersion.import', { import: t('classes.title', { count: 2 }) })}
        height='l'
      >
        {versionUuid && importModalOpen && (
          <TimetableVersionImportClasses
            versionUuid={versionUuid}
            onClose={() => {
              setImportModalOpen(false);
            }}
          />
        )}
      </Modal>
    </>
  );
});
